/**
 * PasswordInput — input[type=password] с toggle visibility (eye / eye-off).
 *
 * 7 states синхронизированы с TextInput: default / hover / focus / error /
 * focus+error / disabled / success.
 *
 * a11y: aria-label на кнопке-переключателе, type меняется реактивно.
 */
import { useState, type KeyboardEvent, type ReactNode } from 'react'
import { Icon } from '@/components/icons/Icon'
import { Tooltip } from '@/components/ui/Tooltip'

type State = 'default' | 'error' | 'success'

interface Props {
  value: string
  onChange: (v: string) => void
  autoComplete?: 'current-password' | 'new-password'
  required?: boolean
  placeholder?: string
  /** @deprecated use `state="error"` */
  hasError?: boolean
  state?: State
  className?: string
  id?: string
  /** Required for password managers / browser autofill (Chrome, 1Password, Bitwarden). */
  name?: string
  'aria-describedby'?: string
  /** Leading icon (mirrors TextInput.leftSlot). Adds pl-10 padding. */
  leftSlot?: ReactNode
}

// См. TextInput.tsx — ring использует Tailwind alpha modifier на RGB-triple токенах.
const stateMap: Record<State, string> = {
  default:
    'border-input-border-default hover:border-border-strong focus:border-border-focus focus:ring-2 focus:ring-border-focus/35',
  error:
    'border-status-danger-border bg-status-danger-bg hover:border-status-danger-text ' +
    'focus:border-status-danger-text focus:ring-[3px] focus:ring-status-danger-text/45',
  success:
    'border-status-success-border hover:border-status-success-text ' +
    'focus:border-status-success-text focus:ring-2 focus:ring-status-success-text/35',
}

export function PasswordInput({
  value,
  onChange,
  autoComplete = 'current-password',
  required,
  placeholder,
  hasError,
  state,
  className,
  id,
  name,
  'aria-describedby': ariaDescribedBy,
  leftSlot,
}: Props) {
  const [visible, setVisible] = useState(false)
  const [capsLock, setCapsLock] = useState(false)
  const resolved: State = hasError ? 'error' : state ?? 'default'

  const checkCapsLock = (e: KeyboardEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>) => {
    // event.getModifierState('CapsLock') / 'Shift' есть на UIEvent (доступен и
    // в KeyboardEvent и в FocusEvent), но типы React не выражают это.
    const native = e.nativeEvent as unknown as { getModifierState?: (k: string) => boolean }
    if (typeof native.getModifierState !== 'function') return
    const caps = native.getModifierState('CapsLock')
    const shift = native.getModifierState('Shift')
    // Caps + Shift = инвертируют друг друга → результирующего «верхнего регистра»
    // нет, индикатор гасим. XOR: горит, если активен ровно один из модификаторов.
    setCapsLock(caps !== shift)
  }

  const padRight = capsLock ? 'pr-[4.5rem]' : 'pr-11'
  const padLeft = leftSlot ? 'pl-10' : 'px-3.5'
  const baseInput =
    `w-full ${padLeft} ${padRight} py-3 border rounded-lg text-sm bg-input-bg-default text-input-text-default placeholder:text-text-muted placeholder:opacity-60 ` +
    'transition-[border-color,box-shadow,background-color,padding] duration-150 focus:outline-none ' +
    'disabled:bg-surface-subtle disabled:text-text-disabled disabled:cursor-not-allowed disabled:opacity-60'

  return (
    <div className={`relative ${className ?? ''}`}>
      {leftSlot && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex text-text-muted pointer-events-none">
          {leftSlot}
        </span>
      )}
      <input
        id={id}
        name={name}
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={checkCapsLock}
        onKeyUp={checkCapsLock}
        onFocus={checkCapsLock}
        placeholder={placeholder}
        aria-invalid={resolved === 'error' || undefined}
        aria-describedby={ariaDescribedBy}
        className={`${baseInput} ${stateMap[resolved]}`}
      />
      {capsLock && (
        <Tooltip content="Caps-Lock is ON" side="top">
          <span
            role="status"
            aria-label="Caps-Lock включён"
            className="absolute right-11 top-1/2 -translate-y-1/2 inline-flex items-center justify-center p-1 rounded text-text-muted hover:text-text-primary transition-colors"
          >
            <Icon name="arrow-big-up-dash" size={20} aria-hidden="true" />
          </span>
        </Tooltip>
      )}
      <button
        type="button"
        // preventDefault на mousedown — клик по глазику не уносит фокус из input
        // и не триггерит onBlur (а значит CapsLock-индикатор остаётся на месте).
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Скрыть пароль' : 'Показать пароль'}
        aria-pressed={visible}
        title={visible ? 'Скрыть пароль' : 'Показать пароль'}
        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-text-muted hover:text-text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-border-focus/30"
      >
        <Icon name={visible ? 'eye-off' : 'eye'} size={20} />
      </button>
    </div>
  )
}
